<template>
  <div id="gallery">
    <FileUpload
      :dialog.sync="uploadDialog"
      :multiple="true"
      @filesUploaded="processUpload($event)"
    />
    <v-dialog v-model="collectionAdd.dialog" max-width="600px">
      <v-card color="card">
        <v-card-title>
          <span class="headline">Add to Collection</span>
        </v-card-title>
        <v-card-text>
          <v-autocomplete
            @keydown.enter="confirmCollection"
            autofocus
            v-model="collectionAdd.id"
            :items="collectionAdd.collections"
            :outlined="!$store.state.site.whitelabel"
            item-text="name"
            item-value="id"
            label="Collection"
          ></v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="blue darken-1"
            text
            @click="collectionAdd.dialog = false"
          >
            Cancel
          </v-btn>
          <v-btn color="blue darken-1" text @click="confirmCollection">
            Add
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="sharing.dialog" max-width="600px">
      <v-card color="card">
        <v-card-title>
          <span class="headline">Share Collection</span>
        </v-card-title>
        <v-card-text>
          <v-select
            label="Public Accessibility"
            v-model="sharing.public"
            :items="sharing.publicOptions"
            :loading="sharing.loading"
            item-text="name"
            item-value="value"
            @change="updateShare"
          ></v-select>
          <template v-if="collection.shareLink">
            This collection can be viewed publicly from
            <a
              target="_blank"
              :href="'/collections/share/' + collection.shareLink"
              >{{ $store.state.site.hostnameWithProtocol }}/collections/share/{{
                collection.shareLink
              }}</a
            >
          </template>
        </v-card-text>
        <v-card-text>
          <v-row>
            <v-col>
              <v-text-field
                v-model="sharing.username"
                label="Username"
                :outlined="!$store.state.site.whitelabel"
              ></v-text-field>
            </v-col>
            <v-col>
              <v-select
                v-model="sharing.role"
                :items="sharing.roles"
                label="Role"
                item-text="name"
                item-value="value"
              ></v-select>
            </v-col>
            <v-col sm="2"> <v-btn @click="addUser" text> Add </v-btn></v-col>
          </v-row>
          <v-data-table :headers="sharing.headers" :items="collection.users">
            <template v-slot:item="row">
              <tr>
                <td>{{ row.item.user.username }}</td>
                <td>
                  <v-checkbox
                    label="Read"
                    v-model="row.item.read"
                    disabled
                  ></v-checkbox>
                </td>
                <td>
                  <v-checkbox
                    label="Write"
                    @change="updateUser(row.item)"
                    v-model="row.item.write"
                    :disabled="row.item.configure"
                  ></v-checkbox>
                </td>
                <td>
                  <v-checkbox
                    label="Conf."
                    @change="updateUser(row.item)"
                    v-model="row.item.configure"
                    :disabled="row.item.recipientId === $store.state.user.id"
                  ></v-checkbox>
                </td>
                <td>
                  <v-btn
                    icon
                    @click="deleteUser(row.item)"
                    text
                    :disabled="row.item.recipientId === $store.state.user.id"
                  >
                    <v-icon> mdi-close </v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="sharing.dialog = false">
            OK
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="settings.dialog" max-width="600px">
      <v-card color="card">
        <v-card-title>Settings</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="collection.name"
            label="Name"
            :outlined="!$store.state.site.whitelabel"
            required
          ></v-text-field>
          <v-file-input
            v-model="settings.image"
            label="Banner"
            @change="updateBanner"
            :loading="settings.imageLoading"
          ></v-file-input>
          <v-btn @click="removeBanner" text color="red">Remove Banner</v-btn>
        </v-card-text>
        <v-card-actions>
          <v-btn
            color="red"
            text
            @click="deleteCollection"
            v-if="permissions.owner"
            >Delete Collection</v-btn
          >
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="settings.dialog = false">
            Cancel
          </v-btn>
          <v-btn color="blue darken-1" text @click="updateSettings">
            Update
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="itemDelete.modal" max-width="600px">
      <v-card class="rounded-xl" elevation="7">
        <v-card-title class="text-h5"
          >Are you sure you want to delete this item?
        </v-card-title>
        <v-card-subtitle
          >You are deleting {{ itemDelete.item.attachment }}, it will be gone
          forever.
        </v-card-subtitle>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            class="rounded-xl"
            color="blue darken-1"
            text
            @click="itemDelete.modal = false"
            >Cancel
          </v-btn>
          <v-btn
            class="rounded-xl"
            color="red darken-1"
            text
            @click="deleteItemConfirm()"
            >Delete
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="edit.modal" max-width="600px">
      <v-card class="rounded-xl" elevation="7">
        <v-card-title>
          <span class="text-h5"
            >Editing Item ({{ edit.item.originalFilename }})</span
          >
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12" md="4" sm="6">
                <v-text-field
                  class="rounded-xl"
                  v-model="edit.item.name"
                  :outlined="!$store.state.site.whitelabel"
                  label="Name"
                  required
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            class="rounded-xl"
            color="blue darken-1"
            text
            @click="edit.modal = false"
          >
            Close
          </v-btn>
          <v-btn
            class="rounded-xl"
            color="blue darken-1"
            text
            @click="editItemConfirm()"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-container
      v-if="!loading"
      :fluid="$store.state.experiments['EXPERIENCE_FLUID']"
    >
      <v-card
        :loading="loading"
        class="rounded-xl d-flex flex-column mb-2"
        elevation="8"
      >
        <v-img
          :src="collectionImage()"
          class="white--text align-end"
          gradient="to bottom, rgba(0,0,0,.1), rgba(0,0,0,1.0)"
          height="200px"
        >
          <template v-slot:placeholder>
            <v-row class="fill-height ma-0" align="center" justify="center">
              <v-progress-circular
                indeterminate
                color="grey lighten-5"
              ></v-progress-circular>
            </v-row>
          </template>
          <v-card-title>
            {{ collection.name }}
            <v-spacer></v-spacer>
            <v-btn
              text
              @click="sharing.dialog = true"
              v-if="!$route.params.type && permissions.configure"
              ><v-icon style="font-size: 20px" class="mr-1">mdi-share</v-icon
              >Collection Sharing</v-btn
            >
            <v-btn
              @click="settings.dialog = true"
              text
              v-if="!$route.params.type && permissions.configure"
              ><v-icon style="font-size: 20px" class="mr-1">mdi-cog</v-icon
              >Settings</v-btn
            >
            <v-btn
              v-else-if="collection.shareLink"
              text
              @click="
                copy(
                  $store.state.site.hostnameWithProtocol +
                    '/collections/share/' +
                    collection.shareLink
                )
              "
            >
              <v-icon style="font-size: 20px" class="mr-1">mdi-link</v-icon>
              Copy Share Link
            </v-btn>
          </v-card-title>
          <v-card-subtitle v-if="collection.users.length">
            <v-icon> mdi-swap-horizontal </v-icon>
            {{ collection.user.username }},
            {{ collection.users.map((user) => user.user.username).join(", ") }}
          </v-card-subtitle>
        </v-img>
      </v-card>
      <v-row v-if="!$vuetify.breakpoint.mobile" class="mt-2 mb-n10">
        <v-col md="7">
          <v-text-field
            class="rounded-xl"
            v-model="show.search"
            :outlined="!$store.state.site.whitelabel"
            append-icon="mdi-magnify"
            label="Search"
            @click:append="getItems(true, true)"
            v-on:keyup.enter="getItems(true, true)"
          ></v-text-field>
        </v-col>
        <v-col md="3">
          <v-select
            v-model="show.selected"
            :items="show.types"
            :outlined="!$store.state.site.whitelabel"
            item-text="name"
            item-value="internalName"
            label="Filter"
            v-on:change="getItems(true, false)"
          ></v-select>
        </v-col>
        <v-col md="2">
          <v-checkbox
            label="Search in files (metadata)"
            v-model="show.metadata"
          >
          </v-checkbox>
        </v-col>
      </v-row>
      <div v-else>
        <v-text-field
          class="rounded-xl mt-5"
          v-model="show.search"
          :outlined="!$store.state.site.whitelabel"
          append-icon="mdi-magnify"
          label="Search"
          @click:append="getItems(true, true)"
          v-on:keyup.enter="getItems(true, true)"
        ></v-text-field>
        <v-select
          v-model="show.selected"
          :items="show.types"
          :outlined="!$store.state.site.whitelabel"
          item-text="name"
          item-value="internalName"
          label="Filter"
          v-on:change="getItems(true, false)"
        ></v-select>
        <v-checkbox
          class="mt-n3"
          label="Search in files (metadata)"
          v-model="show.metadata"
        >
        </v-checkbox>
      </div>
      <GalleryNext
        :items="items"
        :set-page="setPage"
        :page-value="page"
        :getItems="getItems"
        :random-attachment="randomAttachment"
        :supports="{
          multiSelect: false,
          collections: true,
          randomAttachment: true,
          permissions: true
        }"
        :loading="loading"
        @setPage="page = $event"
        :permissions-value="permissions"
      >
        <template v-slot:toolbar-actions="{ item }">
          <v-btn icon @click.prevent.stop="pin(item)" v-if="permissions.write">
            <v-icon v-if="!item.item.pinned">mdi-pin-outline</v-icon>
            <v-icon v-else>mdi-pin</v-icon>
          </v-btn>
          <v-icon v-else-if="item.item.pinned">mdi-pin</v-icon>
        </template>
        <template v-slot:custom-properties="{ item }">
          <div>Owned by: {{ item.user.username }}</div>
        </template>
      </GalleryNext>
    </v-container>
    <v-container v-else>
      <v-row>
        <v-col>
          <v-card class="rounded-xl" elevation="7">
            <v-container class="text-center">
              <p class="text-lg-h4">Loading...</p>
            </v-container>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import AjaxErrorHandler from "@/lib/errorHandler"
import dayjs from "dayjs"
import FileUpload from "../components/FileUpload"
import GalleryNext from "@/components/Gallery"
const advancedFormat = require("dayjs/plugin/advancedFormat")
dayjs.extend(advancedFormat)

export default {
  name: "Gallery",
  components: {
    GalleryNext,
    FileUpload
  },
  data() {
    return {
      settings: {
        dialog: false,
        image: null,
        imageLoading: false
      },
      uploadDialog: false,
      items: {
        items: [],
        pager: []
      },
      collection: {
        users: []
      },
      show: {
        metadata: true,
        selected: "all",
        search: "",
        types: [
          {
            name: "All",
            internalName: "all"
          },
          {
            name: "Images",
            internalName: "image"
          },
          {
            name: "Videos",
            internalName: "video"
          },
          {
            name: "Audio",
            internalName: "audio"
          },
          {
            name: "Text",
            internalName: "text"
          },
          {
            name: "Pastes",
            internalName: "paste"
          },
          {
            name: "Other",
            internalName: "binary"
          }
        ]
      },
      loading: false,
      page: 1,
      itemDelete: {
        modal: false,
        item: []
      },
      edit: {
        modal: false,
        item: []
      },
      collectionAdd: {
        attachmentId: "",
        dialog: false,
        collections: [],
        id: 0
      },
      sharing: {
        dialog: false,
        headers: [
          {
            text: "Username",
            value: "username"
          },
          {
            text: "Read",
            value: "read"
          },
          {
            text: "Write",
            value: "write"
          },
          {
            text: "Configure",
            value: "configure"
          },
          {
            text: "Actions",
            value: "actions"
          }
        ],
        roles: [
          {
            name: "Configure (rwc)",
            value: "rwc"
          },
          {
            name: "Write (rw)",
            value: "rw"
          },
          {
            name: "Read (r)",
            value: "ro"
          }
        ],
        role: "ro",
        username: "",
        users: [],
        public: "nobody",
        publicOptions: [
          {
            name: "Nobody",
            value: "nobody"
          },
          {
            name: "Anyone with the link",
            value: "link"
          }
        ],
        loading: false
      }
    }
  },
  computed: {
    permissions() {
      if (this.$route.params.type === "share") {
        return {
          read: true,
          write: false,
          owner: false,
          joined: false,
          configure: false
        }
      } else if (this.collection.userId === this.$store.state.user.id) {
        return {
          read: true,
          write: true,
          owner: true,
          joined: true,
          configure: true
        }
      } else if (this.collection.recipient) {
        return {
          read: true,
          write: this.collection.recipient.write,
          owner: false,
          joined: true,
          configure: this.collection.recipient.configure
        }
      } else {
        return {
          read: true,
          write: false,
          owner: false,
          joined: false,
          configure: false
        }
      }
    }
  },
  methods: {
    updateBanner() {
      let formData = new FormData()
      formData.append("banner", this.settings.image)
      this.settings.imageLoading = true
      this.axios
        .post(`/api/v1/collections/${this.collection.id}/banner`, formData, {
          headers: {
            "Content-Type": "multipart/form-data"
          }
        })
        .then(() => {
          this.settings.imageLoading = false
          this.$toast.success("Banner updated successfully.")
          this.getCollection()
        })
        .catch((e) => {
          this.settings.imageLoading = false
          AjaxErrorHandler(this.$store)(e)
        })
    },
    removeBanner() {
      this.settings.imageLoading = true
      this.axios
        .delete(`/api/v1/collections/${this.collection.id}/banner`)
        .then(() => {
          this.settings.imageLoading = false
          this.$toast.success("Banner removed successfully.")
          this.getCollection()
        })
        .catch((e) => {
          this.settings.imageLoading = false
          AjaxErrorHandler(this.$store)(e)
        })
    },
    pin(item) {
      this.axios
        .post(`/api/v1/collections/${this.collection.id}/pin/${item.item.id}`)
        .then(() => {
          this.$toast.success("Action completed successfully.")
          this.getItems(true, false, false)
        })
        .catch((e) => {
          AjaxErrorHandler(this.$store)(e)
        })
    },
    setPage(event) {
      this.page = parseInt(event.target.value)
    },
    randomAttachment() {
      this.axios
        .get("/api/v1/collections/random/" + this.collection.id)
        .then((res) => {
          if (this.$store.state.user.openGraph) {
            navigator.clipboard.writeText(
              "https://" +
                this.$store.state.user.domain.domain +
                this.$store.state.user.customPath +
                res.data.attachment
            )
          } else {
            navigator.clipboard.writeText(
              "https://" +
                this.$store.state.user.domain.domain +
                "/i/" +
                res.data.attachment
            )
          }
        })
    },
    confirmCollection() {
      if (!this.collectionAdd.id) return
      this.axios
        .post("/api/v1/collections/attachment", {
          attachmentId: this.collectionAdd.attachmentId,
          collectionId: this.collectionAdd.id
        })
        .then(() => {
          this.collectionAdd.dialog = false
          this.collectionAdd.attachmentId = 0
          this.collectionAdd.id = 0
          this.getItems(true, false, true)
        })
        .catch((e) => {
          this.collectionAdd.dialog = false
          this.collectionAdd.attachmentId = 0
          this.collectionAdd.id = 0
          this.getItems(true, false, true)
          AjaxErrorHandler(this.$store)(e)
        })
    },
    addToCollection(id) {
      if (this.collection.collections.length) {
        this.collection.attachmentId = id
        this.collection.dialog = true
      }
      this.axios.get("/api/v1/collections?write=true").then((res) => {
        this.collection.collections = res.data.rows
        this.collection.attachmentId = id
        this.collection.dialog = true
      })
    },
    updateUser(item) {
      if (item.configure && !item.write) {
        item.write = true
      }
      this.axios
        .put("/api/v1/collections/" + this.collection.id + "/user", {
          id: item.id,
          read: item.read,
          write: item.write,
          configure: item.configure
        })
        .catch((e) => {
          AjaxErrorHandler(this.$store)(e)
        })
    },
    deleteUser(item) {
      this.axios
        .delete(
          "/api/v1/collections/" + this.collection.id + "/user/" + item.id
        )
        .catch((e) => {
          AjaxErrorHandler(this.$store)(e)
        })
    },
    addUser() {
      this.axios
        .post("/api/v1/collections/" + this.collection.id + "/user", {
          username: this.sharing.username,
          read: true,
          write: this.sharing.role === "rw" || this.sharing.role === "rwc",
          configure: this.sharing.role === "rwc"
        })
        .then((res) => {
          this.collection.users.push(res.data)
          this.sharing.username = ""
          this.sharing.role = "ro"
        })
        .catch((e) => {
          AjaxErrorHandler(this.$store)(e)
        })
    },
    updateSettings() {
      this.axios
        .put("/api/v1/collections/" + this.collection.id, {
          name: this.collection.name
        })
        .then(() => {
          this.settings.dialog = false
        })
        .catch((e) => {
          AjaxErrorHandler(this.$store)(e)
        })
    },
    deleteCollection() {
      this.axios
        .delete("/api/v1/collections/" + this.collection.id)
        .then(() => {
          this.$router.push("/collections")
        })
        .catch((e) => {
          AjaxErrorHandler(this.$store)(e)
        })
    },
    removeItem(item) {
      this.axios
        .delete(
          "/api/v1/collections/" + this.collection.id + "/remove/" + item.id
        )
        .then(() => {
          this.$toast.success("Removed from collection")
          this.getItems()
        })
        .catch((e) => {
          AjaxErrorHandler(this.$store)(e)
        })
    },
    collectionImage() {
      if (this.collection.image) {
        return "https://i.troplo.com/i/" + this.collection.image
      } else if (this.collection?.preview?.attachment) {
        return (
          "https://i.troplo.com/i/" +
          this.collection.preview.attachment.attachment
        )
      } else {
        return "https://i.troplo.com/i/a050d6f271c3.png"
      }
    },
    processUpload(files) {
      console.log(files)
      const data = new FormData()
      for (let i = 0; i < files.length; i++) {
        data.append("attachments", files[i])
      }
      this.axios
        .post("/api/v1/gallery/upload/site", data)
        .then(() => {
          this.uploadDialog = false
          this.getItems(true)
        })
        .catch((e) => {
          AjaxErrorHandler(this.$store)(e)
        })
    },
    search() {},
    dayjs(params) {
      return dayjs(params)
    },
    fileSize(fileSize) {
      let i = -1
      let byteUnits = ["KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"]
      do {
        fileSize = fileSize / 1024
        i++
      } while (fileSize > 1024)

      return Math.max(fileSize, 0.1).toFixed(1) + byteUnits[i]
    },
    editItemConfirm() {
      this.axios
        .put("/api/v1/gallery/edit", {
          id: this.edit.item.id,
          name: this.edit.item.name
        })
        .then(() => {
          this.edit.modal = false
          this.edit.item = []
          this.getItems(true)
        })
        .catch((e) => {
          AjaxErrorHandler(this.$store)(e)
        })
    },
    editItem(item) {
      this.edit.item = item
      this.edit.modal = true
    },
    copy(text) {
      navigator.clipboard.writeText(text)
    },
    deleteItem(item) {
      this.itemDelete.item = item
      this.itemDelete.modal = true
    },
    deleteItemConfirm() {
      this.axios
        .post("/api/v1/gallery/delete", {
          id: this.itemDelete.item.id
        })
        .then(() => {
          this.itemDelete.modal = false
          this.itemDelete.item = []
          this.axios.get("/api/v1/userutils/info").then((res) => {
            this.$store.commit("login", res.data)
            this.$vuetify.theme.dark = res.data.darkTheme
            this.$store.commit(
              "setDiscordCache",
              JSON.parse(res.data.discordUserCache)
            )
          })
          this.getItems(true)
        })
        .catch((e) => {
          AjaxErrorHandler(this.$store)(e)
        })
    },
    updateShare() {
      this.sharing.loading = true
      this.axios
        .put("/api/v1/collections/share", {
          id: this.collection.id,
          type: this.sharing.public
        })
        .then((res) => {
          this.sharing.loading = false
          this.collection.shareLink = res.data.shareLink
        })
        .catch((e) => {
          AjaxErrorHandler(this.$store)(e)
        })
    },
    getCollection() {
      if (this.$route.params.type === "share") {
        this.axios
          .get("/api/v1/collections/share/" + this.$route.params.token)
          .then((res) => {
            this.collection = res.data
            if (res.data.shareLink) {
              this.sharing.public = "link"
            } else {
              this.sharing.public = "nobody"
            }
          })
          .catch((e) => {
            AjaxErrorHandler(this.$store)(e)
          })
      } else {
        this.axios
          .get("/api/v1/collections/personal/" + this.$route.params.id)
          .then((res) => {
            this.collection = res.data
            if (res.data.shareLink) {
              this.sharing.public = "link"
            } else {
              this.sharing.public = "nobody"
            }
          })
          .catch((e) => {
            AjaxErrorHandler(this.$store)(e)
          })
      }
    },
    getItems(init, resetPage, load = true) {
      if (resetPage) {
        this.page = 1
      }
      if (load) {
        this.loading = true
      }
      if (this.$route.params.type === "share") {
        this.axios
          .get(
            "/api/v1/collections/share/" +
              this.$route.params.token +
              "/gallery?page=" +
              this.page +
              "&filter=" +
              this.show.selected +
              "&search=" +
              this.show.search +
              "&textMetadata=" +
              this.show.metadata
          )
          .then((res) => {
            this.items = res.data
            this.loading = false
          })
          .catch((e) => {
            this.loading = false
            AjaxErrorHandler(this.$store)(e)
          })
      } else {
        this.axios
          .get(
            "/api/v1/collections/personal/" +
              this.$route.params.id +
              "/gallery?page=" +
              this.page +
              "&filter=" +
              this.show.selected +
              "&search=" +
              this.show.search +
              "&textMetadata=" +
              this.show.metadata +
              "&itemsPerPage=" +
              this.$store.state.attributes.experience.itemsPerPage
          )
          .then((res) => {
            this.items = res.data
            this.loading = false
          })
          .catch((e) => {
            this.loading = false
            AjaxErrorHandler(this.$store)(e)
          })
      }
    }
  },
  mounted() {
    this.getItems(true, true)
    this.getCollection()
  },
  watch: {
    page() {
      this.getItems(true, false)
      this.getCollection()
    },
    $route() {
      this.getItems(true, true)
      this.getCollection()
    }
  }
}
</script>

<style scoped></style>
